import { FEATURE_FLAGS, PAGE_NAME, REDIRECT_LINKS, ROUTES } from "@/app/app-constants";
import { isAllowListedAuthInfo } from "@/utils/allowListUtils";
import { buildLegacyEventHistoryUrl } from "@/utils/urlBuilders";
import type { AuthInfo } from "@/app/types";
import type { PermissionsState } from "@/features/yard360/store/permissions/types";

export interface MenuLink {
  path: string;
  label: string;
  isSelected: (pathname: string) => boolean;
  children?: MenuLink[];
  target?: string;
  rel?: string;
  featureFlag?: string; // Optional feature flag to control visibility
  requiresPermission?: string; // Optional permission key required for visibility (e.g., "agentStudio")
  order?: number; // Optional ordering parameter
}

// Define all possible menu links in a single array
export const ALL_MENU_LINKS: MenuLink[] = [
  {
    path: ROUTES.RYC,
    label: PAGE_NAME.RYC,
    isSelected: (pathname: string) => pathname.startsWith(ROUTES.RYC),
    featureFlag: "enableRYC",
    order: 10, // Lower numbers appear first
  },
  {
    path: ROUTES.APPOINTMENTS,
    label: PAGE_NAME.APPOINTMENTS,
    isSelected: (pathname: string) => pathname === ROUTES.APPOINTMENTS,
    order: 20,
  },
  {
    path: ROUTES.EQUIPMENT,
    label: PAGE_NAME.EQUIPMENT,
    isSelected: (pathname: string) => pathname === ROUTES.EQUIPMENT,
    order: 30,
  },
  {
    path: ROUTES.OWNER_CONFIGURATION,
    label: PAGE_NAME.OWNER_CONFIGURATION,
    isSelected: (pathname: string) => pathname.startsWith(ROUTES.OWNER_CONFIGURATION),
    featureFlag: "enableOwnerConfiguration",
    order: 65,
  },
  {
    path: ROUTES.HOSTLER_POOL,
    label: PAGE_NAME.HOSTLER_POOL,
    isSelected: (pathname: string) => pathname.startsWith(ROUTES.HOSTLER_POOL),
    featureFlag: "enableHostlerPool",
    order: 66,
  },
  {
    path: ROUTES.RYT,
    label: PAGE_NAME.RELAY_YARD_TRACKER,
    featureFlag: "enableRYT",
    isSelected: (pathname: string) => pathname === ROUTES.RYT,
    order: 70,
  },
  {
    // Land users with the queue open on Main Yard / Inbound — matches today's
    // default UX. Once on the page, `useQueueTab` / `useQueueDirection` keep
    // these params in sync as the user toggles tabs, direction, or closes the
    // queue. The `isSelected` matcher only inspects pathname, so the query
    // string doesn't break highlighting.
    path: `${ROUTES.RYTE_TRACKER}?queueTab=mainYard&queueDirection=inbound`,
    label: PAGE_NAME.RYTE_TRACKER,
    featureFlag: "enableRYTE",
    isSelected: (pathname: string) => pathname.startsWith(ROUTES.RYTE_TRACKER),
    order: 15,
  },
  {
    path: ROUTES.AGENT_STUDIO,
    label: PAGE_NAME.AGENT_STUDIO,
    isSelected: (pathname: string) => pathname.startsWith(ROUTES.AGENT_STUDIO),
    featureFlag: "enableAgentStudio",
    requiresPermission: "agentStudio",
    order: 80,
  },
  {
    path: ROUTES.EVENT_REPORT,
    label: PAGE_NAME.EVENT_HISTORY_REPORT,
    isSelected: (pathname: string) => pathname.startsWith(ROUTES.EVENT_REPORT),
    featureFlag: FEATURE_FLAGS.ENABLE_EVENT_HISTORY_NAVIGATION,
    order: 62,
  },
];

// Function to create external links group
export const createExternalLinksGroup = (realm: string, domain: string): MenuLink[] => {
  const { YARD_DESIGNER, YARD_DASHBOARD, RELAY_TRACK_TRACE, REMOTE_GATE } =
    REDIRECT_LINKS[realm]?.[domain] || {};

  // Create other standalone external links
  const standaloneLinks: MenuLink[] = [
    {
      path: YARD_DESIGNER,
      label: PAGE_NAME.YARD_DESIGNER,
      target: "_blank",
      rel: "noopener noreferrer",
      isSelected: () => false,
      order: 60,
    },
    {
      path: YARD_DASHBOARD,
      label: PAGE_NAME.YARD_DASHBOARD,
      target: "_blank",
      rel: "noopener noreferrer",
      isSelected: () => false,
      order: 61,
    },
    {
      path: RELAY_TRACK_TRACE,
      label: PAGE_NAME.RELAY_TRACK_TRACE,
      target: "_blank",
      rel: "noopener noreferrer",
      isSelected: () => false,
      order: 72,
    },
    {
      path: REMOTE_GATE,
      label: PAGE_NAME.REMOTE_GATE,
      target: "_blank",
      rel: "noopener noreferrer",
      isSelected: () => false,
      order: 65,
    },
  ].filter((link) => link.path); // Filter out any undefined paths

  return [...standaloneLinks];
};

// Menu link generator with improved organization
interface GetMenuLinksOptions {
  featureFlags?: Record<string, { enabled: boolean }>;
  authInfo?: AuthInfo;
  permissions?: PermissionsState;
}

/**
 * Determines whether a link that declares `requiresPermission` should be
 * visible, based on the granted permissions returned by the BFF (Chloe RBAC).
 * A link is visible when the user holds at least one granted permission for
 * the corresponding product. Allowlisted users bypass this check.
 */
const hasRequiredPermission = (
  link: MenuLink,
  permissions: PermissionsState | undefined
): boolean => {
  if (!link.requiresPermission) return true;

  const product = permissions?.[link.requiresPermission as keyof PermissionsState];
  return Boolean(product && product.grantedPermissions.length > 0);
};

export const getMenuLinks = ({
  featureFlags = {},
  authInfo,
  permissions,
}: GetMenuLinksOptions): MenuLink[] => {
  let links = [...ALL_MENU_LINKS];
  const isAllowListedUser = isAllowListedAuthInfo(authInfo);
  const isEventHistoryNavigationEnabled =
    isAllowListedUser ||
    featureFlags[FEATURE_FLAGS.ENABLE_EVENT_HISTORY_NAVIGATION]?.enabled === true;
  const tcpBaseUrl =
    authInfo?.realm && authInfo.domain
      ? (REDIRECT_LINKS[authInfo.realm]?.[authInfo.domain]?.TCP_BASEURL ?? "")
      : "";
  const legacyEventHistoryUrl = buildLegacyEventHistoryUrl(tcpBaseUrl);

  if (!isEventHistoryNavigationEnabled && legacyEventHistoryUrl) {
    links = links.map((link) =>
      link.path === ROUTES.EVENT_REPORT
        ? {
            ...link,
            path: legacyEventHistoryUrl,
            featureFlag: undefined,
            target: "_blank",
            rel: "noopener noreferrer",
            isSelected: () => false,
          }
        : link
    );
  }

  // Add external links if realm and domain are provided
  if (authInfo?.realm && authInfo?.domain) {
    links = [...links, ...createExternalLinksGroup(authInfo.realm, authInfo.domain)];
  }

  links = links.filter((link) => {
    if (isAllowListedUser) {
      return true;
    }

    if (link.featureFlag && !featureFlags[link.featureFlag]?.enabled) {
      return false;
    }

    if (!hasRequiredPermission(link, permissions)) {
      return false;
    }

    return true;
  });

  // Sort links by order property
  links.sort((a, b) => (a.order || 999) - (b.order || 999));

  return links;
};

/**
 * Creates a navigation click handler using a currying pattern
 *
 * This function uses a three-level nested function approach (currying):
 * 1. First level: Takes the navigation function from React Router
 * 2. Second level: Takes a specific route path to navigate to
 * 3. Third level: Takes the React click event and performs the navigation
 *
 * Benefits:
 * - Separation of concerns: Navigation logic is separated from UI components
 * - Reusability: The same handler creator can be used across different components
 * - Testability: Makes testing easier by allowing navigation function mocking
 *   without having to mock React Router's useNavigate hook in component tests
 * - Cleaner component code: Components don't need to define inline click handlers
 *
 * Usage example in components:
 * ```
 * const navigate = useNavigate();
 * const handleNavigation = createClickHandler(navigate);
 *
 * // In JSX:
 * <SideMenuLink onClick={handleNavigation('/some/path')}>Link</SideMenuLink>
 * ```
 *
 * Testing example:
 * ```
 * const mockNavigate = jest.fn();
 * const handleClick = createClickHandler(mockNavigate)('/test/path');
 * handleClick(mockEvent);
 * expect(mockNavigate).toHaveBeenCalledWith('/test/path');
 * ```
 *
 * @param navigate - The navigation function from React Router's useNavigate hook
 * @returns A function that takes a path and returns a click event handler
 */
export const createClickHandler =
  (navigate: (path: string) => void) => (path: string) => (_event: React.MouseEvent) =>
    navigate(path);
